import { FC } from 'react';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { format } from 'date-fns';
import { Button, RemoveModal, Table, TableCell, TableRow } from '@forma/forma-ui-kit';
import { hasPermission, PERMISSIONS } from 'helpers/permissions';
import { parseOrgData } from '../../../model/utils';

import { useAppSelector } from 'store/hooks';
import { selectUserPermissions } from 'store/user/userSlice';
import { ICounteragentsItem, ICounteragentType, IDadataItem } from 'interfaces/counteragents.interface';

import styles from '../contragents-list.module.css';

interface ContragentsListProps {
  items?: ICounteragentsItem[],
  outside?: IDadataItem[],
  onRemove: (id: string) => void,
  onAdd: (type: ICounteragentType, values: { [key: string]: string }) => void
}

const ContragentsListPhysics: FC<ContragentsListProps> = ({ items, outside, onRemove, onAdd }) => {
  const { t } = useTranslation();
  const userPermissions = useAppSelector(selectUserPermissions);
  const canCreateContragent = hasPermission(userPermissions ?? [], PERMISSIONS.COUNTERAGENTS.CREATE);
  const canDeleteContragent = hasPermission(userPermissions ?? [], PERMISSIONS.COUNTERAGENTS.DELETE);

  const handleAddCounteragent = async ({ data }: IDadataItem) => {
    if (!canCreateContragent) return;
    const values = parseOrgData(data);
    // fixme
    // values[322]?.length === innIndividualLength || values[2006]?.length === innIndividualLength ? 'individual' : 'entity'
    onAdd('person', values);
  };

  return (
    <div className={classNames(styles.root, 'styled-scrollbar')}>
      <Table columns={[
        { children: t('fullname_short'), className: styles.fio },
        { children: t('birthdate'), className: styles.date },
        { children: t('inn'), className: styles.inn },
        { children: '', className: styles.buttons },
      ]}>
        {!!items?.length && (
          items.map(({ id, name, birthday, inn }) => (
            <TableRow key={id}>
              <TableCell className={classNames(styles.cellWithLink, styles.fio)} title={t('fullname_short')}>
                <Link to={`/contragents/${id}`} className={styles.link}>
                  {name || t('no_fullname')}
                </Link>
              </TableCell>
              <TableCell className={classNames(styles.cellWithLink, styles.date)} title={t('birthdate')}>
                <Link to={`/contragents/${id}`} className={styles.link}>
                  {birthday ? format(new Date(birthday), 'dd.MM.yyyy') : t('no_date')}
                </Link>
              </TableCell>
              <TableCell className={classNames(styles.cellWithLink, styles.inn)} title={t('inn')}>
                <Link to={`/contragents/${id}`} className={styles.link}>
                  {inn || t('no_inn')}
                </Link>
              </TableCell>
              <TableCell className={styles.buttons}>
                <RemoveModal
                  control={
                    <Button
                      title={t('delete')}
                      className={styles.remove}
                      viewStyle="textLight"
                      icon={<ReactSVG src="/icons/trash.svg" wrapper="span" />}
                      disabled={!canDeleteContragent}
                    />
                  }
                  onRemove={() => onRemove(id)}
                  title={t('do_yo_want_delete_contragent')}
                />
              </TableCell>
            </TableRow>
          ))
        )}
        {!!(outside?.length) && (
          <TableRow>
            <TableCell colSpan={4}>
              <div className={styles.subtitle}>{t('global_search_from_egrul')}:</div>
            </TableCell>
          </TableRow>
        )}
        {outside?.map(({ value, unrestricted_value, data }) => (
          <TableRow key={data.inn ?? value}>
            <TableCell
              className={classNames(styles.cellWithLink, styles.juristicName)}
              onClick={() => handleAddCounteragent({ value, unrestricted_value, data })}
              title={t('juristic_name')}
            >
              <div className={styles.link}>
                {data.name?.full_with_opf || data.name?.full || value || (data.fio && `${data.fio.surname} ${data.fio.name} ${data.fio.patronymic}`)}
              </div>
            </TableCell>
            <TableCell
              className={classNames(styles.cellWithLink, styles.inn)}
              onClick={() => handleAddCounteragent({ value, unrestricted_value, data })}
              title={t('inn')}
            >
              <div className={styles.link}>
                {data.inn}
              </div>
            </TableCell>
            <TableCell className={styles.buttons} onClick={() => handleAddCounteragent({ value, unrestricted_value, data })}>
              {' '}
            </TableCell>
          </TableRow>
        ))}
      </Table>
    </div>
  );
};

export default ContragentsListPhysics;
